/**
 * Encore response-compression envelope (house trap — see
 * modules/systemAdmin-v2/src/services/client.ts, the reference port of legacy
 * APIV3.js:44-83): several metaship-backend services register
 * highCompressionMiddleware, which gzips ANY response payload >1KB into
 * `{__compressed, data|result: "<base64-gzip>"}` — and Encore's serde strips
 * every undeclared field, so the wire body is just `{ result: "H4sIA…" }`
 * (a STRING). Without this unwrap every consumer breaks only once the payload
 * crosses 1KB, masquerading as a shape bug per caller — the CRM tenant app's
 * sign-in hung exactly here ("e.filter is not a function":
 * /user-access/organisation with 48 orgs crossed 1KB; boer's smaller lists
 * may not).
 *
 * A decompression failure returns the raw body so the caller surfaces a real
 * error rather than this helper throwing something opaque.
 */

const GZIP_BASE64_PREFIX = "H4sIA";

function looksLikeGzipBase64(value: unknown): value is string {
  return typeof value === "string" && value.length > 8 && value.startsWith(GZIP_BASE64_PREFIX);
}

async function gunzipBase64(base64: string): Promise<unknown> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return JSON.parse(await new Response(stream).text());
}

export async function unwrapCompressedBody(body: unknown): Promise<unknown> {
  if (!body || typeof body !== "object") return body;
  const envelope = body as { data?: unknown; result?: unknown };
  const packed = looksLikeGzipBase64(envelope.data)
    ? envelope.data
    : looksLikeGzipBase64(envelope.result)
      ? envelope.result
      : null;
  if (!packed) return body;
  try {
    return await gunzipBase64(packed);
  } catch {
    return body;
  }
}
